import {
  FC,
  Fragment,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Check,
  CheckCheck,
  ChevronsDown,
  CircleAlert,
  Clock,
  Flag,
  MapPin,
  MessageSquareDashed,
  Navigation2,
} from 'lucide-react';
import { ChatMessage, DeliveryState } from '@component/DriverChat/types';
import {
  buildMessageListItems,
  formatChatShortTime,
  formatChatTime,
  isChatEnabledStatus,
} from '@component/DriverChat/helpers';
import { useAppSelector } from '@store/hooks';
import { selectUsers } from '@store/slices/users/usersSlice';
import { DriverAvatar } from '@component/DriverChat/DriverAvatar/DriverAvatar';

type MessageListProps = {
  messages: ChatMessage[];
  firstUnreadId: string | null;
  isLoading: boolean;
  isError: boolean;
  /** The backend sends no typing events; kept for the UI only. */
  isTyping?: boolean;
  driverName: string;
  driverImage?: string;
  hasMore: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
  onReload: () => void;
  onRetry: (message: ChatMessage) => void;
};

const NEAR_BOTTOM_PX = 80;

const Receipt: FC<{ status?: DeliveryState }> = ({ status = 'sent' }) => {
  switch (status) {
    case 'sending':
      return (
        <>
          <Clock size={12} strokeWidth={2} aria-hidden="true" />
          Sending
        </>
      );
    case 'delivered':
      return (
        <>
          <CheckCheck size={14} strokeWidth={2} aria-hidden="true" />
          Delivered
        </>
      );
    case 'read':
      return (
        <span className="dc-meta__read">
          <CheckCheck size={14} strokeWidth={2} aria-hidden="true" />
          Read
        </span>
      );
    default:
      return (
        <>
          <Check size={13} strokeWidth={2} aria-hidden="true" />
          Sent
        </>
      );
  }
};

const FailedMeta: FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="dc-meta is-failed">
    <CircleAlert size={13} strokeWidth={2} aria-hidden="true" />
    Not delivered ·
    <button type="button" className="dc-link" onClick={onRetry}>
      Retry
    </button>
  </div>
);

const SystemIcon: FC<{ message: ChatMessage }> = ({ message }) => {
  const props = { size: 14, strokeWidth: 2, 'aria-hidden': true } as const;
  if (message.statusId == null) return <MapPin {...props} />;
  return isChatEnabledStatus(message.statusId) ? (
    <Navigation2 {...props} />
  ) : (
    <Flag {...props} />
  );
};

export const MessageList: FC<MessageListProps> = ({
  messages,
  firstUnreadId,
  isLoading,
  isError,
  isTyping = false,
  driverName,
  driverImage,
  hasMore,
  isLoadingOlder,
  onLoadOlder,
  onReload,
  onRetry,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const didInitialScrollRef = useRef(false);
  const lastCountRef = useRef(messages.length);
  const [hasNewBelow, setHasNewBelow] = useState(false);
  // Scroll height before older history was prepended, to keep the reader in place.
  const olderAnchorRef = useRef<number | null>(null);

  const loadOlder = () => {
    olderAnchorRef.current = listRef.current?.scrollHeight ?? null;
    onLoadOlder();
  };

  const { userInfo } = useAppSelector(selectUsers);
  const items = useMemo(
    () => buildMessageListItems(messages, firstUnreadId, userInfo),
    [messages, firstUnreadId, userInfo]
  );
  const firstName = driverName.split(' ')[0];

  const scrollToBottom = () => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    isNearBottomRef.current = true;
    setHasNewBelow(false);
  };

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !messages.length) return;

    if (olderAnchorRef.current != null) {
      list.scrollTop += list.scrollHeight - olderAnchorRef.current;
      olderAnchorRef.current = null;
      lastCountRef.current = messages.length;
      return;
    }

    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      const divider = list.querySelector<HTMLElement>('.dc-new-divider');
      if (divider) list.scrollTop = divider.offsetTop - 16;
      else scrollToBottom();
      lastCountRef.current = messages.length;
      return;
    }

    const added = messages.length > lastCountRef.current;
    lastCountRef.current = messages.length;
    const ownMessage = messages[messages.length - 1]?.sender === 'DISPATCHER';

    if (isNearBottomRef.current || (added && ownMessage)) scrollToBottom();
    else if (added) setHasNewBelow(true);
  }, [messages]);

  useLayoutEffect(() => {
    // A failed older-page request must not leave a stale anchor behind.
    if (!isLoadingOlder) olderAnchorRef.current = null;
  }, [isLoadingOlder]);

  useLayoutEffect(() => {
    if (isTyping && isNearBottomRef.current) scrollToBottom();
  }, [isTyping]);

  const onScroll = () => {
    const list = listRef.current;
    if (!list) return;
    isNearBottomRef.current =
      list.scrollHeight - list.scrollTop - list.clientHeight < NEAR_BOTTOM_PX;
    if (isNearBottomRef.current && hasNewBelow) setHasNewBelow(false);
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="dc-log__skeleton" aria-label="Loading messages">
          <span className="dc-skeleton" style={{ width: '62%', height: 34 }} />
          <span
            className="dc-skeleton is-own"
            style={{ width: '48%', height: 34 }}
          />
          <span className="dc-skeleton" style={{ width: '70%', height: 50 }} />
          <span
            className="dc-skeleton is-own"
            style={{ width: '40%', height: 34 }}
          />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="dc-empty">
          <span className="dc-empty__icon">
            <CircleAlert size={26} strokeWidth={1.6} aria-hidden="true" />
          </span>
          <b>Messages couldn't load</b>
          <span>Check your connection and try again.</span>
          <button type="button" className="dc-link" onClick={onReload}>
            Try again
          </button>
        </div>
      );
    }
    if (!messages.length) {
      return (
        <div className="dc-empty">
          <span className="dc-empty__icon">
            <MessageSquareDashed
              size={26}
              strokeWidth={1.6}
              aria-hidden="true"
            />
          </span>
          <b>No messages yet</b>
          <span>
            Send {firstName} a quick update. Common replies are one tap away
            below.
          </span>
        </div>
      );
    }

    const rendered = items.map((item) => {
      if (item.kind === 'day') {
        return (
          <div key={item.key} className="dc-day">
            {item.label}
          </div>
        );
      }
      if (item.kind === 'new') {
        return (
          <div key={item.key} className="dc-new-divider">
            New messages
          </div>
        );
      }
      if (item.kind === 'system') {
        return (
          <div key={item.key} className="dc-system">
            <SystemIcon message={item.message} />
            {item.message.text}
            <time>· {formatChatShortTime(item.message.createdAt)}</time>
          </div>
        );
      }

      // Every dispatcher message sits on the right (mine and colleagues'); only the
      // driver is on the left, with an avatar.
      const isOwn = item.sender === 'DISPATCHER';
      // A name is shown above everyone except me: the driver, or a colleague.
      const authorName = item.isOwn
        ? undefined
        : item.sender === 'DRIVER'
          ? item.authorName || driverName
          : item.authorName;
      const last = item.messages[item.messages.length - 1];
      return (
        <div
          key={item.key}
          className={`dc-group ${isOwn ? 'is-own' : 'is-driver'}`}
        >
          {!isOwn && (
            <DriverAvatar name={driverName} image={driverImage} size="xs" />
          )}
          <div className="dc-group__stack">
            {authorName && <div className="dc-author">{authorName}</div>}
            {item.messages.map((message) => (
              <Fragment key={message.id}>
                <div
                  className={`dc-bubble ${
                    message.status === 'failed' ? 'is-failed' : ''
                  } ${message.status === 'sending' ? 'is-sending' : ''}`}
                >
                  {message.text}
                </div>
                {isOwn && message.status === 'failed' && message !== last && (
                  <FailedMeta onRetry={() => onRetry(message)} />
                )}
              </Fragment>
            ))}
            {isOwn && last.status === 'failed' ? (
              <FailedMeta onRetry={() => onRetry(last)} />
            ) : (
              <div className="dc-meta">
                {isOwn ? (
                  <>
                    {formatChatTime(last.createdAt)} ·
                    <Receipt status={last.status} />
                  </>
                ) : (
                  <>{formatChatTime(last.createdAt)}</>
                )}
              </div>
            )}
          </div>
        </div>
      );
    });

    return (
      <>
        {hasMore && (
          <button
            type="button"
            className="dc-link dc-load-older"
            disabled={isLoadingOlder}
            onClick={loadOlder}
          >
            {isLoadingOlder ? 'Loading…' : 'Load earlier messages'}
          </button>
        )}
        {rendered}
      </>
    );
  };

  return (
    <div className="dc-log-wrap">
      <div
        ref={listRef}
        className="dc-log"
        role="log"
        aria-live="polite"
        aria-label={`Messages with ${driverName}`}
        onScroll={onScroll}
      >
        {renderBody()}
        {isTyping && !isLoading && (
          <div className="dc-typing">
            <DriverAvatar name={driverName} image={driverImage} size="xs" />
            <span
              className="dc-typing__dots"
              aria-label={`${firstName} is typing`}
            >
              <i />
              <i />
              <i />
            </span>
          </div>
        )}
      </div>
      {hasNewBelow && (
        <button type="button" className="dc-jump" onClick={scrollToBottom}>
          <ChevronsDown size={14} strokeWidth={2} aria-hidden="true" />
          New message
        </button>
      )}
    </div>
  );
};
